import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getIntegrationsApi,
  getIntegrationApi,
  createIntegrationApi,
  createVivaIntegrationApi,
  updateIntegrationApi,
  deleteIntegrationApi,
} from "../api/integrationsApi";
import type { RestCreateVivaIntegrationBody } from "../api/integrationsApi";
import type { ChainId, IntegrationId, StoreId } from "@KassaroAB/shared/types/ids";
import type { RestCreateIntegration, RestUpdateIntegration } from "@KassaroAB/shared/types/integrations/integration";

const integrationsQueryKey = (chainId: ChainId | null | undefined) => [
  "chains",
  chainId,
  "integrations",
];

export const useGetIntegrationsQuery = (chainId: ChainId | null) => {
  return useQuery({
    queryKey: integrationsQueryKey(chainId),
    queryFn: () => getIntegrationsApi(chainId!),
    enabled: !!chainId,
  });
};

export const useGetIntegrationQuery = (
  chainId: ChainId | null,
  integrationId: IntegrationId | null,
) => {
  return useQuery({
    queryKey: [...integrationsQueryKey(chainId), integrationId],
    queryFn: () => getIntegrationApi(chainId!, integrationId!),
    enabled: !!chainId && !!integrationId,
  });
};

export const useCreateIntegrationMutation = (
  chainId: ChainId | null,
  storeId?: StoreId | null,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (integration: RestCreateIntegration) =>
      createIntegrationApi(chainId!, integration, storeId ?? undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationsQueryKey(chainId) });
    },
  });
};

export const useCreateVivaIntegrationMutation = (chainId: ChainId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: RestCreateVivaIntegrationBody) =>
      createVivaIntegrationApi(chainId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationsQueryKey(chainId) });
    },
  });
};

export const useUpdateIntegrationMutation = (chainId: ChainId | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      integrationId,
      integration,
    }: {
      integrationId: IntegrationId;
      integration: Partial<RestUpdateIntegration>;
    }) => updateIntegrationApi(chainId!, integrationId, integration),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationsQueryKey(chainId) });
    },
  });
};

export const useDeleteIntegrationMutation = (chainId: ChainId | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (integrationId: IntegrationId) =>
      deleteIntegrationApi(chainId!, integrationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationsQueryKey(chainId) });
    },
  });
};
